import { AuthApi } from '@/features/api/services/authApi.js'
import {
  clearTemporaryToken,
  getTemporaryToken,
  getTemporaryTokenTenantId,
  setTemporaryToken,
} from '@/features/auth/auth.utils.js'

export const LOGIN_TENANT_NO_ACCESS_CODE = 'You_Have_No_Access_For_This_Tenant'

export class LoginTenantAccessError extends Error {
  constructor(errorCode) {
    super(`LoginTenant failed: ${errorCode}`)
    this.name = 'LoginTenantAccessError'
    this.errorCode = errorCode
  }
}

export function findTenantMobileProfileId(myDetails, clubId) {
  if (!myDetails || !clubId) {
    return null
  }

  const tenant = (myDetails.myTenants || []).find(item => item.tenantId === clubId)
  return tenant?.mobileProfileId || null
}

// LoginTenant can answer 200 OK with an error code string instead of a token
// (e.g. "You_Have_No_Access_For_This_Tenant"). Only accept JWT-shaped values
// so error codes are never stored as a bearer token.
function isJwtLikeToken(value) {
  return typeof value === 'string' && value.split('.').length === 3
}

function extractLoginTenantToken(response) {
  if (!response) {
    return null
  }

  // Observed uat2 behavior: the body is a bare JSON string (error code or
  // token), but object shapes are also accepted defensively.
  const candidates = [response, response.auth_token, response.value?.auth_token, response.value]

  return candidates.find(isJwtLikeToken) || null
}

function extractLoginTenantErrorCode(response) {
  if (typeof response === 'string') {
    return response
  }

  if (typeof response?.value === 'string') {
    return response.value
  }

  return 'unknown response'
}

/**
 * Invalidates the current temporary tenant token (BE logout + local clear).
 * Safe to call when no temporary token exists.
 */
export async function invalidateTemporaryTenantToken() {
  const temporaryToken = getTemporaryToken()
  clearTemporaryToken()

  if (!temporaryToken) {
    return
  }

  try {
    await AuthApi.logoutWithToken(temporaryToken)
  } catch (e) {
    console.error('[temporaryTenantSession] Failed to invalidate temporary token:', e)
  }
}

/**
 * Makes sure a temporary tenant token exists for the given club when the
 * tenant exposes a mobileProfileId. Returns true when a temporary token is
 * active for the club, false when the club uses the legacy main-token flow.
 */
export async function ensureTemporaryTenantToken(myDetails, clubId) {
  const mobileProfileId = findTenantMobileProfileId(myDetails, clubId)

  if (!mobileProfileId) {
    return false
  }

  if (getTemporaryToken() && getTemporaryTokenTenantId() === clubId) {
    return true
  }

  // Stale token from another tenant — invalidate before issuing a new one.
  await invalidateTemporaryTenantToken()

  const response = await AuthApi.loginTenant(mobileProfileId)
  const token = extractLoginTenantToken(response)

  if (!token) {
    throw new LoginTenantAccessError(extractLoginTenantErrorCode(response))
  }

  setTemporaryToken(token, clubId)
  return true
}
